/**
 * Centralised DOM access layer.
 *
 * Only this file calls document.getElementById.
 * Everything else calls the getter/setter functions below.
 * Setters also persist settings to localStorage.
 */

import { saveSettings, type Settings } from "./state/persistence";

// ─── Private element refs ─────────────────────────────────────────────────────

const _startInput          = document.getElementById("start-input")        as HTMLInputElement;
const _destInput           = document.getElementById("dest-input")         as HTMLInputElement;
const _routeBtn            = document.getElementById("route-btn")          as HTMLButtonElement;
const _locBtn              = document.getElementById("current-loc-btn")   as HTMLButtonElement;
const _settingsBtn          = document.getElementById("settings-btn")       as HTMLButtonElement;
const _settingsPanel        = document.getElementById("settings-panel")     as HTMLDivElement;
const _routeInfoEl         = document.getElementById("route-info")          as HTMLDivElement;
const _statusEl            = document.getElementById("status")             as HTMLDivElement;
const _spinnerEl           = document.getElementById("route-spinner")      as HTMLSpanElement;
const _minTimeEl           = document.getElementById("min-time")         as HTMLInputElement;
const _maxTimeEl           = document.getElementById("max-time")         as HTMLInputElement;
const _poiRadiusEl         = document.getElementById("poi-radius")         as HTMLInputElement;
const _showSearchAreaEl    = document.getElementById("show-search-area")  as HTMLInputElement;
const _showOnlySelectedEl  = document.getElementById("show-only-selected") as HTMLInputElement;
const _includeToiletsEl    = document.getElementById("include-toilets")   as HTMLInputElement;
const _poiDetourMaxEl      = document.getElementById("poi-detour-max")   as HTMLInputElement;
const _poiPanel            = document.getElementById("poi-panel")         as HTMLDivElement;
const _poiToggle           = document.getElementById("poi-toggle-btn")   as HTMLButtonElement;
const _poiListEl           = document.getElementById("poi-list")           as HTMLDivElement;
const _poiCountEl          = document.getElementById("poi-count")          as HTMLSpanElement;
const _diagEl              = document.getElementById("diag")               as HTMLDivElement;
const _liveModeEl        = document.getElementById("live-mode")        as HTMLInputElement;
const _liveProgressEl    = document.getElementById("live-progress")    as HTMLDivElement;
const _liveGpsFreshnessEl = document.getElementById("live-gps-freshness") as HTMLDivElement;

// ─── Persisted settings snapshot ─────────────────────────────────────────────

/** Current settings — written by every setter, read by saveSettings(). */
let current: Settings = {
  minHours:         0,
  maxHours:         2,
  radiusKm:         3,
  showSearchArea:   false,
  showOnlySelected: false,
  includeToilets:   false,
  poiDetourMaxMin:  15,
};

/** Sync element state → current settings object. */
function snapshot(): void {
  current = {
    minHours:         parseFloat(_minTimeEl.value) || 0,
    maxHours:         parseFloat(_maxTimeEl.value) || 2,
    radiusKm:         parseFloat(_poiRadiusEl.value) || 3,
    showSearchArea:   _showSearchAreaEl.checked,
    showOnlySelected: _showOnlySelectedEl.checked,
    includeToilets:   _includeToiletsEl.checked,
    poiDetourMaxMin:  parseFloat(_poiDetourMaxEl.value) || 15,
  };
}

function persist(): void {
  snapshot();
  saveSettings(current);
}

/** Call once on page load to restore saved settings into DOM elements. */
export function restoreSettings(s: Settings): void {
  current = { ...s };
  _minTimeEl.value         = String(s.minHours);
  _maxTimeEl.value         = String(s.maxHours);
  _poiRadiusEl.value       = String(s.radiusKm);
  _showSearchAreaEl.checked   = s.showSearchArea;
  _showOnlySelectedEl.checked = s.showOnlySelected;
  _includeToiletsEl.checked   = s.includeToilets;
  _poiDetourMaxEl.value      = String(s.poiDetourMaxMin ?? 15);
}

// ─── Exported element refs (read-only use cases) ───────────────────────────────

export const startInput        = _startInput;
export const destInput         = _destInput;
export const routeBtn          = _routeBtn;
export const locBtn            = _locBtn;
export const settingsBtn       = _settingsBtn;
export const settingsPanel     = _settingsPanel;
export const routeInfoEl       = _routeInfoEl;
export const statusEl          = _statusEl;
export const spinnerEl         = _spinnerEl;
export const minTimeEl         = _minTimeEl;
export const maxTimeEl         = _maxTimeEl;
export const poiRadiusEl       = _poiRadiusEl;
export const showSearchAreaEl  = _showSearchAreaEl;
export const showOnlySelectedEl = _showOnlySelectedEl;
export const includeToiletsEl  = _includeToiletsEl;
export const poiDetourMaxEl    = _poiDetourMaxEl;
export const poiPanel          = _poiPanel;
export const poiToggle         = _poiToggle;
export const poiListEl         = _poiListEl;
export const poiCountEl        = _poiCountEl;
export const diagEl             = _diagEl;
export const liveModeEl        = _liveModeEl;
export const liveProgressEl    = _liveProgressEl;
export const liveGpsFreshnessEl = _liveGpsFreshnessEl;

// ─── Getters ─────────────────────────────────────────────────────────────────

export function getMinHours():            number  { return parseFloat(_minTimeEl.value) || 0; }
export function getMaxHours():            number  { return parseFloat(_maxTimeEl.value) || 2; }
export function getRadiusKm():            number  { return parseFloat(_poiRadiusEl.value) || 3; }
export function isShowSearchArea():       boolean { return _showSearchAreaEl.checked; }
export function isShowOnlySelected():     boolean { return _showOnlySelectedEl.checked; }
export function isIncludeToilets():       boolean { return _includeToiletsEl.checked; }
export function getPoiDetourMaxMin(): number { return parseFloat(_poiDetourMaxEl.value) || 15; }

// ─── Setters (also persist) ─────────────────────────────────────────────────

export function setMinHours(v: number): void {
  _minTimeEl.value = String(v);
  persist();
}
export function setMaxHours(v: number): void {
  _maxTimeEl.value = String(v);
  persist();
}
export function setRadiusKm(v: number): void {
  _poiRadiusEl.value = String(v);
  persist();
}
export function setShowSearchArea(v: boolean): void {
  _showSearchAreaEl.checked = v;
  persist();
}
export function setShowOnlySelected(v: boolean): void {
  _showOnlySelectedEl.checked = v;
  persist();
}
export function setIncludeToilets(v: boolean): void {
  _includeToiletsEl.checked = v;
  persist();
}
export function setPoiDetourMax(v: number): void {
  _poiDetourMaxEl.value = String(v);
  persist();
}

// ─── Live mode helpers ────────────────────────────────────────────────────────

export function isLiveMode(): boolean    { return _liveModeEl.checked; }
export function setLiveMode(v: boolean): void {
  _liveModeEl.checked = v;
}
export function setLiveProgress(html: string): void {
  _liveProgressEl.innerHTML = html;
}
